import { useEffect, useState } from 'react';
import { getReports, updateReportStatus, deleteReport, deleteMultipleReports } from '../services/reportService';
import { formatTimestamp } from '../utils/time';
import type { Report } from '../types';

export default function ReportsPage() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<Report | null>(null);
  const [answer, setAnswer] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'answered' | 'completed'>('all');

  useEffect(() => { loadReports(); }, []);

  async function loadReports() {
    setLoading(true);
    try { setReports(await getReports()); }
    finally { setLoading(false); }
  }

  const filtered = filter === 'all' ? reports : reports.filter(r => r.status === filter);

  async function handleStatusChange(id: string, status: Report['status'], ans?: string) {
    await updateReportStatus(id, status, ans);
    loadReports();
    if (detail?.id === id) setDetail({ ...detail, status, answer: ans ?? detail.answer });
  }

  async function handleDeleteSelected() {
    if (!selected.size || !confirm(`${selected.size}개 신고를 삭제하시겠습니까?`)) return;
    await deleteMultipleReports([...selected]);
    setSelected(new Set());
    loadReports();
  }

  const statusBadge = (status: string) => {
    const cls = status === 'pending' ? 'bg-yellow-100 text-yellow-800'
      : status === 'answered' ? 'bg-blue-100 text-blue-800'
      : 'bg-green-100 text-green-800';
    const label = status === 'pending' ? '대기' : status === 'answered' ? '답변' : '완료';
    return <span className={`px-2 py-0.5 text-xs rounded-full ${cls}`}>{label}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">신고 관리</h1>
        <button onClick={loadReports} className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">새로고침</button>
      </div>

      <div className="flex gap-2 mb-4">
        {(['all', 'pending', 'answered', 'completed'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm rounded-lg border ${filter === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
          >
            {f === 'all' ? `전체 (${reports.length})` : f === 'pending' ? '대기' : f === 'answered' ? '답변' : '완료'}
          </button>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 p-2 bg-blue-50 rounded-lg">
          <span className="text-sm">{selected.size}개 선택</span>
          <button onClick={handleDeleteSelected} className="text-sm text-red-600 hover:underline">삭제</button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-gray-500 hover:underline">해제</button>
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold mb-3">신고 상세</h3>
            <dl className="space-y-2 text-sm mb-4">
              <div className="flex"><dt className="w-24 text-gray-500">신고자</dt><dd>{detail.reporterNickname}</dd></div>
              <div className="flex"><dt className="w-24 text-gray-500">대상</dt><dd>{detail.targetNickname || '-'}</dd></div>
              <div className="flex"><dt className="w-24 text-gray-500">상태</dt><dd>{statusBadge(detail.status)}</dd></div>
              <div className="flex"><dt className="w-24 text-gray-500">시간</dt><dd>{formatTimestamp(detail.createdAt)}</dd></div>
            </dl>
            <div className="p-3 bg-gray-50 rounded-lg text-sm mb-4">{detail.reason}</div>
            {detail.answer && <div className="p-3 bg-blue-50 rounded-lg text-sm mb-4"><strong>답변:</strong> {detail.answer}</div>}

            <textarea
              value={answer}
              onChange={e => setAnswer(e.target.value)}
              placeholder="답변 작성..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none mb-3"
            />
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => { handleStatusChange(detail.id, 'answered', answer); setAnswer(''); }} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg">답변 저장</button>
              <button onClick={() => handleStatusChange(detail.id, 'completed')} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg">완료 처리</button>
              <button onClick={() => { deleteReport(detail.id); setDetail(null); loadReports(); }} className="px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg">삭제</button>
              <button onClick={() => setDetail(null)} className="px-3 py-1.5 text-sm text-gray-500 border border-gray-300 rounded-lg">닫기</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-3 w-10"><input type="checkbox" onChange={() => { if (selected.size === filtered.length) setSelected(new Set()); else setSelected(new Set(filtered.map(r => r.id))); }} /></th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">상태</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">신고자</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">대상</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">사유</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500">시간</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">로딩 중...</td></tr>
            ) : filtered.map(report => (
              <tr key={report.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => { setDetail(report); setAnswer(report.answer || ''); }}>
                <td className="px-3 py-2" onClick={e => { e.stopPropagation(); const s = new Set(selected); s.has(report.id) ? s.delete(report.id) : s.add(report.id); setSelected(s); }}>
                  <input type="checkbox" checked={selected.has(report.id)} readOnly />
                </td>
                <td className="px-3 py-2">{statusBadge(report.status)}</td>
                <td className="px-3 py-2">{report.reporterNickname}</td>
                <td className="px-3 py-2">{report.targetNickname || '-'}</td>
                <td className="px-3 py-2 truncate max-w-xs">{report.reason}</td>
                <td className="px-3 py-2 text-gray-500">{formatTimestamp(report.createdAt, 'short')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">{filtered.length}건</div>
      </div>
    </div>
  );
}
